/* global google */
import { useState, useCallback, useEffect } from 'react';
import { useLogger } from 'hook/error.hook';
import debounce from 'lodash.debounce';

export const useGoogleAutocomplete = (setAddressHandler, disableFlag) => {
  const logger = useLogger({
    name: 'googleAutocomplete.hook',
    categories: ['address', 'forms'],
  });

  // from normalize ticket
  const debounce_time = 500;
  const chars_minimum_before_calling_API = 3;
  const maximum_suggestions = null;

  const [cache, setCache] = useState({});
  const [cacheGetDetail, setCacheGetDetail] = useState({});
  const [highlighted, setHighlighted] = useState(0);
  const [autosuggestions, setAutosuggestions] = useState([]);
  const [sessionToken, setSessionToken] = useState(null);
  const [searchTerm, setSearchTerm] = useState(null);

  const handleAutocompleteDebounce = e => {
    if (e.target) {
      setSearchTerm(e.target.value);
    }
  };
  //eslint-disable-next-line
  const handleAutocomplete = useCallback(
    debounce(handleAutocompleteDebounce, debounce_time),
    [],
  );

  useEffect(() => {
    if (searchTerm) {
      loadAutoSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  if (disableFlag) {
    return { autosuggestions };
  }

  if (!window.google) {
    logger.error(
      'Autocomplete: Google Maps JavaScript API library not loaded.',
    );
    return { autosuggestions };
  }

  if (!window.google.maps.places) {
    logger.error('Autocomplete: Google Maps Places library not loaded.');
    return { autosuggestions };
  }

  const autoCompleteService = new google.maps.places.AutocompleteService();
  const el = document.getElementById('mapsHolder');
  if (!el) {
    return { autosuggestions };
  }
  const placeService = new google.maps.places.PlacesService(
    new google.maps.Map(el),
  );

  const loadAutoSuggestions = () => {
    let len = searchTerm.length;

    if (typeof autoCompleteService === 'object') {
      if (!searchTerm) {
        setAutosuggestions([]);
      }

      if (len >= chars_minimum_before_calling_API) {
        if (cache[searchTerm]) {
          setAutosuggestions(cache[searchTerm]);
        } else {
          autoCompleteService.getPlacePredictions(
            {
              input: searchTerm,
              componentRestrictions: {
                country: 'us',
              },
              types: ['address'],
              sessionToken: getSessionToken(),
            },
            autocompleteCallback.bind(this, searchTerm),
          );
        }
      }
    }
  };

  function getSessionToken() {
    let finalSessionToken = sessionToken;
    if (!finalSessionToken) {
      finalSessionToken = new google.maps.places.AutocompleteSessionToken();
      setSessionToken(finalSessionToken);
    }
    return finalSessionToken;
  }

  function autofill(selection) {
    const { terms, placeId } = selection;
    let street = '';
    let unit = '';
    let zip = '';
    let city = '';
    let state = '';

    //   if (context === "current-street") {
    if (terms.length === 3) {
      street = terms[0].value;
      zip = '';
      unit = '';
      state = terms[1].value;
    }
    if (terms.length === 4) {
      street = terms[0].value;
      zip = '';
      unit = '';
      city = terms[1].value;
      state = terms[2].value;
    }
    if (terms.length === 5) {
      street = terms[0].value + ' ' + terms[1].value;
      zip = '';
      unit = '';
      city = terms[2].value;
      state = terms[3].value;
    }
    if (terms.length === 6) {
      street = terms[0].value + ' ' + terms[1].value;
      zip = '';
      unit = terms[2].value;
      city = terms[3].value;
      state = terms[4].value;
    }

    // check to see if cache exists
    if (cacheGetDetail[placeId]) {
      zip = cacheGetDetail[placeId];

      const autofill = {
        zip,
        city,
        state,
        street,
        unit,
      };
      setAddressHandler(autofill);
    } else {
      placeService.getDetails(
        {
          placeId: placeId,
          sessionToken: getSessionToken(),
          fields: ['address_component'],
        },
        (place, status) => {
          if (status === 'OK') {
            place.address_components.map(component => {
              if (component.types[0] === 'postal_code') {
                zip = component.long_name;
                setCacheGetDetail({ ...cacheGetDetail, [placeId]: zip });
              }
              return null;
            });
          }
          const autofill = {
            zip,
            city,
            state,
            street,
            unit,
          };
          setAddressHandler(autofill);
        },
      );
      setSessionToken(null);
    }
    collapseAutocomplete();
  }

  function collapseAutocomplete() {
    setAutosuggestions([]);
  }

  function autocompleteCallback(key, predictions, status) {
    if (status === 'OK') {
      const suggestions = predictions
        .map((p, idx) => ({
          id: p.id,
          description: p.description,
          placeId: p.place_id,
          active: false,
          index: idx,
          matchedSubstrings: p.matched_substrings,
          terms: p.terms,
          types: p.types,
          format: p.structured_formatting,
        }))
        .filter((_, index) =>
          maximum_suggestions ? index < maximum_suggestions : true,
        );
      setCache({
        ...cache,
        [key]: suggestions,
      });
      setAutosuggestions(suggestions);
    }
  }

  const handleKeyDown = e => {
    // 13: Enter-Key, 27: Escape-Key, 38: Down-Arrow, 40: Up-Arrow

    if (e.keyCode === 27) {
      handleBlur();
    }
    if (e.keyCode === 13 && autosuggestions.length > 0) {
      e.preventDefault();
      handleSelection(highlighted);
      collapseAutocomplete();
    }

    if (e.keyCode === 38 && highlighted > 0) {
      e.preventDefault();
      setHighlighted(highlighted - 1);
    }

    if (e.keyCode === 40 && highlighted < autosuggestions.length - 1) {
      e.preventDefault();
      setHighlighted(highlighted + 1);
    }
  };

  const handleSelection = id => {
    if (autosuggestions[id]) {
      autofill(autosuggestions[id]);
    }
  };

  const handleBlur = e => {
    collapseAutocomplete();
  };

  const handleMouseEnter = e => {
    let index = e.currentTarget.attributes.getNamedItem('data')
      ? parseInt(e.currentTarget.attributes.getNamedItem('data').value)
      : null;
    if (index || index === 0) {
      setHighlighted(index);
    }
  };
  const handleMouseLeave = e => {
    let index = e.currentTarget.attributes.getNamedItem('data')
      ? parseInt(e.currentTarget.attributes.getNamedItem('data').value)
      : null;
    if (index || index === 0) {
      setHighlighted(null);
    }
  };
  const handleMouseDown = e => {
    let index = e.currentTarget.attributes.getNamedItem('data')
      ? parseInt(e.currentTarget.attributes.getNamedItem('data').value)
      : null;
    handleSelection(index);
  };

  return {
    loading: true,
    autosuggestions,
    highlighted,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleKeyDown,
    handleAutocomplete,
    handleBlur,
  };
};
